import type { CSSProperties } from 'react';
import { sf } from '../../themeConfig/sisuForgeTokens';

/** Shared inline styles — Sisu Forge tokens; overrides Croham globals */

export const font = sf.fontSans;

export const page: CSSProperties = {
  boxSizing: 'border-box',
  padding: 'clamp(16px, 4vw, 24px) clamp(14px, 4vw, 18px) max(120px, calc(88px + env(safe-area-inset-bottom, 0px)))',
  fontFamily: font,
  fontSize: 'clamp(15px, 2.6vw, 16px)',
  lineHeight: 1.45,
  letterSpacing: '0',
  color: sf.text,
  textShadow: 'none',
  WebkitFontSmoothing: 'antialiased',
};

export const h1: CSSProperties = {
  margin: 0,
  fontFamily: sf.fontDisplay,
  fontSize: 'clamp(1.25rem, 4.2vw, 1.65rem)',
  fontWeight: 700,
  lineHeight: 1.12,
  letterSpacing: '-0.04em',
  color: sf.text,
  textShadow: 'none',
};

export const h2: CSSProperties = {
  margin: 0,
  fontFamily: sf.fontDisplay,
  fontSize: '1rem',
  fontWeight: 600,
  letterSpacing: '-0.02em',
  color: sf.text,
  textShadow: 'none',
};

export const bodyMuted: CSSProperties = {
  margin: '8px 0 0',
  fontFamily: font,
  fontSize: 14,
  lineHeight: 1.5,
  color: sf.textMuted,
  textShadow: 'none',
};

export const labelSmall: CSSProperties = {
  margin: 0,
  fontFamily: sf.fontDisplay,
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.16em',
  textTransform: 'uppercase' as const,
  color: sf.textSubtle,
};

export const rowBetween: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 16,
  marginBottom: 24,
  flexWrap: 'wrap',
};

export const card: CSSProperties = {
  borderRadius: sf.radiusLg,
  border: `1px solid ${sf.border}`,
  backgroundColor: sf.surface,
  backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.02) 0%, transparent 100%)',
  padding: 20,
  textAlign: 'center' as const,
  boxSizing: 'border-box',
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
};

export const cardSolid: CSSProperties = {
  borderRadius: sf.radiusLg,
  border: `1px solid ${sf.border}`,
  backgroundColor: sf.surface,
  padding: 16,
  boxSizing: 'border-box',
};

export const btnPrimary: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  minHeight: sf.touchMin,
  padding: '0 16px',
  fontFamily: font,
  fontSize: 15,
  fontWeight: 600,
  letterSpacing: '0.06em',
  color: sf.text,
  backgroundColor: sf.primaryDeep,
  border: `1px solid ${sf.steelBlue}`,
  borderRadius: sf.radiusMd,
  cursor: 'pointer',
  boxSizing: 'border-box',
  textShadow: 'none',
  WebkitAppearance: 'none' as const,
  appearance: 'none',
  transition: `background-color ${sf.transitionFast}, box-shadow ${sf.transitionFast}, border-color ${sf.transitionFast}`,
};

export const btnSecondary: CSSProperties = {
  ...btnPrimary,
  color: sf.text,
  backgroundColor: sf.surface,
  border: `1px solid ${sf.border}`,
  boxShadow: 'none',
};

export const packingCard: CSSProperties = {
  ...cardSolid,
  border: `1px solid ${sf.border}`,
  backgroundColor: sf.surface,
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)',
  padding: 18,
};

/** Prefer `className="tb-link-ghost"` in styles.css for machined link look */
export const btnGhost: CSSProperties = {
  marginTop: 12,
  padding: 0,
  fontFamily: font,
  fontSize: 15,
  fontWeight: 600,
  letterSpacing: '0.03em',
  color: sf.accent,
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  textDecoration: 'none',
  textShadow: 'none',
};

export const input: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  minHeight: 52,
  padding: '14px 16px',
  fontFamily: font,
  fontSize: 16,
  lineHeight: 1.4,
  color: sf.text,
  backgroundColor: sf.surfaceElevated,
  border: `1px solid ${sf.border}`,
  borderRadius: sf.radiusMd,
  outline: 'none',
  textShadow: 'none',
  transition: `border-color ${sf.transitionFast}, box-shadow ${sf.transitionFast}`,
};

export const textarea: CSSProperties = {
  ...input,
  minHeight: 120,
  resize: 'vertical' as const,
};

export const select: CSSProperties = {
  ...input,
  cursor: 'pointer',
};

export const linkBack: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 16,
  fontFamily: font,
  fontSize: 15,
  fontWeight: 500,
  color: sf.textMuted,
  textDecoration: 'none',
  textShadow: 'none',
};

export const tabBar: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 8,
  marginTop: 16,
  padding: 6,
  borderRadius: sf.radiusLg,
  border: `1px solid ${sf.border}`,
  backgroundColor: sf.surface,
  boxSizing: 'border-box',
};

export const tabBtn = (active: boolean): CSSProperties => ({
  minHeight: sf.touchMin,
  padding: '10px 8px',
  fontFamily: sf.fontDisplay,
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: '0.06em',
  border: `1px solid ${active ? 'rgba(58, 141, 255, 0.55)' : 'transparent'}`,
  borderRadius: sf.radiusSm,
  cursor: 'pointer',
  textShadow: 'none',
  WebkitAppearance: 'none' as const,
  appearance: 'none',
  color: active ? sf.text : sf.textMuted,
  backgroundColor: active ? sf.primaryDeep : 'transparent',
  boxShadow: active
    ? 'inset 0 0 0 1px rgba(58, 141, 255, 0.35), 0 1px 0 rgba(58, 141, 255, 0.12)'
    : 'none',
  transition: `background-color ${sf.transitionFast}, border-color ${sf.transitionFast}, box-shadow ${sf.transitionFast}`,
});

export const listItemLink: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '18px 16px',
  borderRadius: sf.radiusLg,
  border: `1px solid ${sf.border}`,
  backgroundColor: sf.surfaceElevated,
  textDecoration: 'none',
  color: 'inherit',
  boxSizing: 'border-box',
  transition: `border-color ${sf.transitionFast}, box-shadow ${sf.transitionFast}`,
};

export const modalBackdrop: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 60,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
  backgroundColor: 'rgba(0, 0, 0, 0.82)',
  boxSizing: 'border-box',
};

export const modalPanel: CSSProperties = {
  width: '100%',
  maxWidth: 420,
  maxHeight: 'min(90vh, 640px)',
  overflowY: 'auto',
  borderRadius: sf.radiusLg,
  border: `1px solid ${sf.border}`,
  backgroundColor: sf.surface,
  padding: 22,
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.55), inset 0 1px 0 rgba(255,255,255,0.04)',
  boxSizing: 'border-box',
};

export const flexGap: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
};

export const flexRowGap: CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  gap: 10,
  flexWrap: 'wrap',
};
